import { Logo } from '@/components/common/Logo';
import { CreateOrganisationModal } from '@/features/organisations/components/CreateOrganisationModal';
import { OrganisationSwitcher } from '@/features/organisations/components/OrganisationSwitcher';
import { cn } from '@/utils/cn';
import { useAuthStore } from '@/store/auth.store';
import {
  Briefcase,
  BookOpen,
  Building2,
  ExternalLink,
  HelpCircle,
  Settings,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface SidebarProps {
  navigation: NavigationItem[];
  currentPath: string;
  onClose?: () => void;
  isMobile?: boolean;
  estClientSeul: boolean;
  aDesEntreprises: boolean;
}

export function Sidebar({
  navigation,
  currentPath,
  onClose,
  isMobile = false,
  estClientSeul,
  aDesEntreprises,
}: SidebarProps) {
  const { t } = useTranslation();
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const role = useAuthStore(s => s.user?.role);

  return (
    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-neutral-900 px-6 pb-4">
      {/* Header avec logo et bouton fermer (mobile) */}
      <div className="flex h-16 shrink-0 items-center justify-between">
        <div className="relative mt-8 flex h-[80px] items-center overflow-hidden">
          <Logo width={140} height={140} className="brightness-0 invert" />
        </div>
        {isMobile && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="-mr-2 p-2 text-neutral-400 transition-colors hover:text-white"
            aria-label={t('navigation.closeMenu', 'Fermer le menu')}
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Organisation Switcher — le sélecteur de CABINET. Une entreprise
          cliente n'a pas de cabinet à choisir : le lui montrer laisse croire
          qu'elle pourrait en changer depuis ici. */}
      {!estClientSeul && (
        <OrganisationSwitcher onCreateClick={() => setShowCreateOrg(true)} />
      )}

      {/* Navigation */}
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              {navigation.map(item => {
                const isActive = currentPath === item.href;
                const Icon = item.icon;

                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={cn(
                        'group flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-medium transition-all duration-200',
                        isActive
                          ? 'from-brand-600 to-brand-500 bg-gradient-to-r text-white shadow-lg'
                          : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-5 w-5 shrink-0 transition-colors',
                          isActive
                            ? 'text-white'
                            : 'group-hover:text-brand-400 text-neutral-400'
                        )}
                      />
                      {item.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>

          {/* Section Projets/Équipes */}
          <li>
            <div className="text-xs leading-6 font-semibold tracking-wider text-neutral-400 uppercase">
              {t('sidebar.reporting', 'Reporting')}
            </div>
            <ul role="list" className="-mx-2 mt-2 space-y-1">
              {/* <li>
                <Link
                  to="/projects/main"
                  className="group flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-medium text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white"
                >
                  <span className="group-hover:text-brand-400 group-hover:border-brand-500 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-neutral-700 bg-neutral-800 text-[0.625rem] font-medium text-neutral-400 transition-colors">
                    A
                  </span>
                  <span className="truncate">Analyse</span>
                </Link>
              </li> */}
              <li>
                <Link
                  to="/notifications"
                  className={cn(
                    'group flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-medium text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white',
                    currentPath === '/notifications'
                      ? 'from-brand-600 to-brand-500 bg-gradient-to-r text-white shadow-lg'
                      : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'group-hover:text-brand-400 group-hover:border-brand-500 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-neutral-700 bg-neutral-800 text-[0.625rem] font-medium text-neutral-400 transition-colors',
                      currentPath === '/notifications'
                        ? 'text-white'
                        : 'text-neutral-400'
                    )}
                  >
                    N
                  </span>
                  <span className="truncate">
                    {t('navigation.notifications', 'Notifications')}
                  </span>
                </Link>
              </li>
            </ul>
          </li>

          {/* Support, Documentation et Paramètres en bas */}
          <li className="mt-auto space-y-3">
            <Link
              to="/support"
              className={cn(
                'group -mx-2 flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-medium transition-colors hover:bg-neutral-800 hover:text-white',
                currentPath === '/support'
                  ? 'from-brand-600 to-brand-500 bg-gradient-to-r text-white shadow-lg'
                  : 'text-neutral-400'
              )}
            >
              <HelpCircle className="group-hover:text-brand-400 h-5 w-5 shrink-0 transition-colors" />
              {t('navigation.support', 'Support')}
            </Link>
            <a
              href="https://docs.innov-paie.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="group -mx-2 flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-medium text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white"
            >
              <BookOpen className="group-hover:text-brand-400 h-5 w-5 shrink-0 transition-colors" />
              <span className="flex flex-1 items-center justify-between">
                {t('navigation.documentation', 'Documentation')}
                <ExternalLink className="h-3.5 w-3.5 opacity-50" />
              </span>
            </a>
            {/* [C2] N'apparaît QUE pour qui tient un rôle dans une entreprise :
                un gestionnaire de cabinet ne voit pas un menu vide. C'est un
                TERRITOIRE (l'espace du client), pas une tâche — la seule
                justification d'une entrée de menu selon la règle du 16/08. */}
            {/* En bas pour un CABINET dont un membre tient aussi un rôle chez
                un client. Pour un client seul, l'entrée est déjà en tête de
                barre — l'afficher deux fois serait un doublon. */}
            {aDesEntreprises && !estClientSeul && (
              <Link
                to="/mon-entreprise"
                className={cn(
                  'group -mx-2 flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-medium transition-colors hover:bg-neutral-800 hover:text-white',
                  currentPath === '/mon-entreprise'
                    ? 'from-brand-600 to-brand-500 bg-gradient-to-r text-white shadow-lg'
                    : 'text-neutral-400'
                )}
              >
                <Briefcase className="group-hover:text-brand-400 h-5 w-5 shrink-0 transition-colors" />
                {t('navigation.monEntreprise', 'Mon entreprise')}
              </Link>
            )}
            {/* « Organisation » gère le CABINET : ses membres, ses réglages.
                Une entreprise cliente n'en a pas — et le lui montrer lui
                donnerait à croire qu'elle administre quelque chose du cabinet.
                Son équivalent à elle est « Mon entreprise », juste au-dessus. */}
            {!estClientSeul && (
              <Link
                to="/organisations"
                className={cn(
                  'group -mx-2 flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-medium transition-colors hover:bg-neutral-800 hover:text-white',
                  currentPath === '/organisations'
                    ? 'from-brand-600 to-brand-500 bg-gradient-to-r text-white shadow-lg'
                    : 'text-neutral-400'
                )}
              >
                <Building2 className="group-hover:text-brand-400 h-5 w-5 shrink-0 transition-colors" />
                {t('navigation.organisation', 'Organisation')}
              </Link>
            )}
            {/* « Paramètres » reste : ce sont les réglages du COMPTE — mot de
                passe, langue, notifications. Ils appartiennent à la personne,
                pas au cabinet. */}
            <Link
              to="/settings"
              className={cn(
                'group -mx-2 flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-medium transition-colors hover:bg-neutral-800 hover:text-white',
                currentPath === '/settings'
                  ? 'from-brand-600 to-brand-500 bg-gradient-to-r text-white shadow-lg'
                  : 'text-neutral-400'
              )}
            >
              <Settings className="group-hover:text-brand-400 h-5 w-5 shrink-0 transition-colors" />
              {t('navigation.settings', 'Paramètres')}
            </Link>
            {role === 'admin' && (
              <Link
                to="/admin"
                className={cn(
                  'group -mx-2 flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-medium transition-colors hover:bg-neutral-800 hover:text-white',
                  currentPath === '/admin'
                    ? 'from-brand-600 to-brand-500 bg-gradient-to-r text-white shadow-lg'
                    : 'text-neutral-400'
                )}
              >
                <ShieldCheck className="group-hover:text-brand-400 h-5 w-5 shrink-0 transition-colors" />
                {t('navigation.admin', 'Admin')}
              </Link>
            )}
          </li>
        </ul>
      </nav>
      <CreateOrganisationModal
        isOpen={showCreateOrg}
        onClose={() => setShowCreateOrg(false)}
      />
    </div>
  );
}
